import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Calendar, Clock } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";
import DownloadBrochureButton from "@/components/ledger/DownloadBrochureButton";
import { POSTS, buildBlogIndexJsonLd } from "./posts.mjs";

const SITE = "https://www.viloraimpex.com";

export default function BlogIndexPage() {
  useSeo({
    title: "Export Journal — Indian Rice & Spice Trade Insights | Vilora Impex",
    description:
      "Practical, founder-led guides on Indian rice export — basmati varieties, processing, ports, documentation, MOQ, private label and the trade-house operations that move grain to twelve destination ports.",
    path: "/blog",
    image: `${SITE}/og.jpg`,
    imageAlt: "Vilora Impex Export Journal — Indian rice trade insights",
    ogType: "website",
    jsonLd: buildBlogIndexJsonLd(),
  });

  const posts = useMemo(
    () => Object.values(POSTS).sort((a, b) => new Date(b.datePublished) - new Date(a.datePublished)),
    [],
  );

  const categories = useMemo(() => {
    const set = new Set(posts.map((p) => p.category));
    return ["All", ...Array.from(set)];
  }, [posts]);

  const [activeCategory, setActiveCategory] = useState("All");
  const visiblePosts = useMemo(
    () => (activeCategory === "All" ? posts : posts.filter((p) => p.category === activeCategory)),
    [activeCategory, posts],
  );

  return (
    <main className="ledger blog-index-page relative min-h-screen">
      <BlogIndexStyles />
      <ScrollProgress />
      <Navbar />

      <section className="blog-index-hero paper">
        <div className="blog-index-shell">
          <nav className="blog-index-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">Journal</span>
          </nav>

          <span className="brass-ribbon blog-index-eyebrow">The export journal</span>

          <h1 className="display blog-index-title">Notes from the desk</h1>

          <p className="blog-index-deck">
            Practical, founder-led guides on Indian rice export — basmati varieties, processing,
            ports, documentation, MOQ, private label, and the trade-house operations that move
            grain from Gujarat to twelve destination ports.
          </p>

          <div className="blog-index-brochure">
            <DownloadBrochureButton variant="ghost" label="Download the export brochure (PDF)" />
          </div>

          <div className="blog-index-filter" role="tablist" aria-label="Filter posts by category">
            {categories.map((c) => (
              <button
                key={c}
                type="button"
                role="tab"
                aria-selected={activeCategory === c}
                className={`blog-index-filter-chip ${activeCategory === c ? "is-active" : ""}`}
                onClick={() => setActiveCategory(c)}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="blog-index-list" role="feed">
            {visiblePosts.map((p, i) => (
              <article key={p.slug} className="blog-index-card">
                <div className="blog-index-card-folio">
                  <span className="folio">N&deg; {String(i + 1).padStart(2, "0")}</span>
                  <span className="rule rule-grow" />
                  <span className="folio" style={{ color: "var(--ink-mute)" }}>{p.category.toUpperCase()}</span>
                </div>
                <Link to={`/blog/${p.slug}`} className="blog-index-card-link">
                  {p.heroImage ? (
                    <figure className="blog-index-card-thumb">
                      <img src={p.heroImage} alt={p.heroAlt || p.h1} loading="lazy" width="480" height="270" />
                    </figure>
                  ) : null}
                  <div className="blog-index-card-text">
                    <h2 className="display blog-index-card-title">{p.h1}</h2>
                    <p className="blog-index-card-excerpt">{p.excerpt}</p>
                    <div className="blog-index-card-meta">
                      <span className="blog-index-card-meta-item">
                        <Calendar size={12} strokeWidth={1.6} />
                        <span>{new Date(p.datePublished).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })}</span>
                      </span>
                      <span className="blog-index-card-meta-item">
                        <Clock size={12} strokeWidth={1.6} />
                        <span>{p.readingTime} min</span>
                      </span>
                      <span className="blog-index-card-meta-item blog-index-card-cta">
                        <span>Read</span>
                        <ArrowRight size={13} strokeWidth={1.7} />
                      </span>
                    </div>
                  </div>
                </Link>
              </article>
            ))}
            {visiblePosts.length === 0 ? (
              <p className="blog-index-empty">No posts in this category yet.</p>
            ) : null}
          </div>
        </div>
      </section>

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}

function BlogIndexStyles() {
  return (
    <style>{`
      .blog-index-page { color: var(--ink); }
      .blog-index-shell { max-width: 1080px; margin: 0 auto; padding: clamp(48px, 6vw, 88px) clamp(20px, 4vw, 40px) 64px; }
      .blog-index-breadcrumb {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px; letter-spacing: 0.18em; text-transform: uppercase;
        color: var(--ink-mute); display: flex; gap: 10px; align-items: center;
        margin-bottom: 18px;
      }
      .blog-index-breadcrumb a { color: var(--brass-deep); text-decoration: none; }
      .blog-index-eyebrow { display: inline-block; margin-bottom: 18px; }
      .blog-index-title {
        font-size: clamp(40px, 6vw, 76px);
        line-height: 0.98;
        margin: 0 0 18px;
      }
      .blog-index-deck {
        font-family: 'Crimson Pro', Georgia, serif;
        font-size: clamp(18px, 1.8vw, 22px);
        line-height: 1.55;
        color: var(--ink-soft);
        max-width: 720px;
        margin: 0 0 22px;
      }
      .blog-index-brochure { margin: 0 0 28px; }
      .blog-index-filter {
        display: flex; flex-wrap: wrap; gap: 8px;
        margin: 0 0 36px;
        padding: 0 0 24px;
        border-bottom: 1px solid rgba(168,132,61,0.20);
      }
      .blog-index-filter-chip {
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        color: var(--ink-mute);
        background: transparent;
        border: 1px solid rgba(168,132,61,0.32);
        padding: 7px 13px;
        cursor: pointer;
        transition: color 200ms ease, border-color 200ms ease, background 200ms ease;
      }
      .blog-index-filter-chip:hover { color: var(--brass-deep); border-color: var(--brass); }
      .blog-index-filter-chip.is-active {
        color: var(--bone);
        background: var(--ink);
        border-color: var(--ink);
      }
      .blog-index-list { display: grid; gap: 36px; }
      .blog-index-card {
        padding-bottom: 32px;
        border-bottom: 1px solid rgba(168,132,61,0.20);
      }
      .blog-index-card:last-child { border-bottom: none; }
      .blog-index-card-folio { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; }
      .blog-index-card-link {
        display: grid;
        grid-template-columns: 1fr;
        gap: 18px;
        text-decoration: none;
        color: var(--ink);
      }
      @media (min-width: 768px) {
        .blog-index-card-link {
          grid-template-columns: 280px 1fr;
          gap: 28px;
          align-items: start;
        }
      }
      .blog-index-card-thumb {
        margin: 0;
        overflow: hidden;
        border: 1px solid rgba(168,132,61,0.28);
        background: var(--paper-2, #ECE3CE);
        aspect-ratio: 16 / 9;
        display: block;
      }
      .blog-index-card-thumb img {
        width: 100%; height: 100%;
        object-fit: cover;
        display: block;
        transition: transform 500ms ease;
      }
      .blog-index-card-link:hover .blog-index-card-thumb img { transform: scale(1.04); }
      .blog-index-card-text { min-width: 0; }
      .blog-index-card-title {
        font-size: clamp(22px, 2.8vw, 30px);
        line-height: 1.12;
        margin: 0 0 12px;
        transition: color 200ms ease;
      }
      .blog-index-card-link:hover .blog-index-card-title { color: var(--brass-deep); }
      .blog-index-card-excerpt {
        font-family: 'Crimson Pro', serif;
        font-size: clamp(16px, 1.4vw, 18px);
        line-height: 1.65;
        color: var(--ink-soft);
        margin: 0 0 14px;
        max-width: 760px;
      }
      .blog-index-card-meta {
        display: flex; flex-wrap: wrap; gap: 16px;
        font-family: 'JetBrains Mono', monospace;
        font-size: 11px;
        letter-spacing: 0.12em;
        text-transform: uppercase;
        color: var(--ink-mute);
      }
      .blog-index-card-meta-item { display: inline-flex; align-items: center; gap: 6px; }
      .blog-index-card-cta { color: var(--brass-deep); margin-left: auto; }
      .blog-index-card-link:hover .blog-index-card-cta { color: var(--vermilion, #C8431F); }
      .blog-index-empty {
        font-family: 'Crimson Pro', serif;
        font-style: italic;
        color: var(--ink-mute);
        padding: 24px 0;
      }
    `}</style>
  );
}
